import React, { useEffect, useState, useCallback } from "react";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import { Row, Col } from "react-bootstrap";
import { useNavigate, Link } from "react-router-dom";
import { API_BASE_URL } from "../config";
import { useAppContext } from "../context/AppContext";
import {
  DynamicComboCard,
  normalizeImage,
  getItemKey,
} from "./components/ProductCard";
import SeoHelmet from "./components/SeoHelmet";
import FloatingWidgets from "./components/FloatingWidgets";

const getEffectivePrice = (item) => {
  const price = Number(item?.price || 0);
  const offerPrice = Number(item?.offerPrice || 0);
  if (offerPrice > 0 && offerPrice < price) return offerPrice;
  return price;
};

const getOldPrice = (item) => {
  const price = Number(item?.price || 0);
  const offerPrice = Number(item?.offerPrice || 0);
  const mrp = Number(item?.mrp || 0);

  if (offerPrice > 0 && offerPrice < price) return mrp || price;
  if (mrp > price) return mrp;
  return 0;
};

const parseWeightToGrams = (weight) => {
  const value = String(weight || "")
    .toLowerCase()
    .trim();

  if (value.endsWith("kg")) {
    return Number(value.replace("kg", "").trim()) * 1000;
  }

  if (value.endsWith("g")) {
    return Number(value.replace("g", "").trim());
  }

  return 0;
};

const formatWeight = (grams) => {
  if (grams >= 1000) {
    return `${grams / 1000}kg`;
  }
  return `${grams}g`;
};

const prepareCombo = (combo) => {
  const basePrice = getEffectivePrice(combo);
  const baseMrp = getOldPrice(combo);
  // Treat the price in DB as the 1kg price
  const baseWeightGrams = 1000;

  const optionsInGrams = [250, 500, 1000];
  const weightOptions = optionsInGrams.map((grams) => {
    const ratio = grams / baseWeightGrams;
    return {
      weight: formatWeight(grams),
      price: Math.round(basePrice * ratio),
      mrp: Math.round(baseMrp * ratio),
    };
  });

  const defaultOption =
    weightOptions.find((o) => o.weight === "250g") || weightOptions[0];

  return {
    ...combo,
    weightOptions,
    weights: weightOptions.map((o) => o.weight),
    img: normalizeImage(combo.mainImage || combo.image || combo.img),
    comboType: combo.comboType || combo.categoryName || combo.category?.name || "Combo",
    categoryName: combo.comboType || combo.categoryName || combo.category?.name || "Combo",
    price: defaultOption.price,
    old_price: defaultOption.mrp,
    quantity: combo.quantity || 1,
    net_wt: defaultOption.weight,
    items: combo.items || combo.productsCount || combo.itemCount || 1,
    slug: combo.slug || combo._id,
  };
};

export default function Combo() {
  const navigate = useNavigate();
  const { addToCart, user } = useAppContext();
  const [combos, setCombos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCombos = async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL}/api/content/combos?status=Published`,
        );
        const json = await res.json();
        const rawCombos = Array.isArray(json?.data)
          ? json.data
          : Array.isArray(json)
            ? json
            : [];

        const publishedCombos = rawCombos
          .filter((item) => !item.isDeleted && item.status === "Published")
          .map(prepareCombo);

        setCombos(publishedCombos);
      } catch (error) {
        console.error("Error fetching combos:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCombos();
  }, []);

  const updateListItemByKey = (listSetter, itemKey, updater) => {
    listSetter((prevList) =>
      prevList.map((item) =>
        getItemKey(item) === itemKey ? updater(item) : item,
      ),
    );
  };

  const handleQuantityChange = (itemKey, listSetter, delta) => {
    updateListItemByKey(listSetter, itemKey, (item) => ({
      ...item,
      quantity: Math.max(1, Number(item.quantity || 1) + delta),
    }));
  };

  const handleProductWeightChange = (itemKey, listSetter, newWeight) => {
    updateListItemByKey(listSetter, itemKey, (item) => {
      const selected = item.weightOptions?.find((o) => o.weight === newWeight);
      return {
        ...item,
        net_wt: newWeight,
        price: selected?.price ?? item.price,
        old_price: selected?.mrp ?? item.old_price,
      };
    });
  };

  const handleAddToCart = (productToAdd, redirect = false) => {
    const newItem = {
      productId: productToAdd.slug || productToAdd._id,
      slug: productToAdd.slug,
      name: productToAdd.name,
      img: productToAdd.img,
      categoryName: productToAdd.categoryName,
      price: productToAdd.price,
      old_price: productToAdd.old_price,
      quantity: productToAdd.quantity || 1,
      net_wt: productToAdd.net_wt,
      weights: productToAdd.weights,
    };

    addToCart(newItem, redirect);

    if (redirect) {
      navigate("/checkout");
      return;
    }

    const cartOffcanvas = document.getElementById("cartmini");
    if (cartOffcanvas && window.bootstrap && window.bootstrap.Offcanvas) {
      const bsOffcanvas =
        window.bootstrap.Offcanvas.getOrCreateInstance(cartOffcanvas);
      bsOffcanvas.show();
    }
  };

  return (
    <>
      <SeoHelmet
        title="Combos & Offers | A1 Bakery & Sweets"
        description="Grab the best deals on our curated sweet and snack combos. Perfect for celebrations, gifting, or everyday cravings."
        keywords="sweet combos, snack deals, A1 offers, gift boxes"
      />
      <Navbar />

      <header className="header-combo">
        <div className="header-content">
          <div className="breadcrumb__list mb-20">
            <span>
              <Link to="/">Home</Link>
            </span>
            <span className="dvdr">
              <i className="fa-regular fa-angle-right"></i>
            </span>
            <span>Combos & Offers</span>
          </div>
          <h1>Perfect Combos for Every Craving & Celebration</h1>
          <p>
            Sweeten your celebrations with irresistible deals and signature
            flavors.
          </p>
        </div>
      </header>

      <main className="combo-container mt-90 mb-90">
        <section className="combo-page">
          <div className="container">
            <div className="bd-section__title-wrapper text-center mb-35">
              <div className="bd-sm__section-title mb-10">
                <h2>Combos</h2>
              </div>
              <div className="bd-subcribe__text">
                <p>
                  Because sharing sweets is sharing joy. Let your combo box tell
                  a story.
                </p>
                <hr />
              </div>
            </div>

            <Row>
              {loading ? (
                <Col xs={12} className="text-center py-5">
                  <div className="spinner-border text-danger" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </Col>
              ) : combos.length > 0 ? (
                combos.map((combo, index) => (
                  <Col
                    key={combo._id || index}
                    lg={3}
                    md={4}
                    sm={6}
                    xs={12}
                    className="mb-4"
                  >
                    <DynamicComboCard
                      product={combo}
                      originalIndex={index}
                      listSetter={setCombos}
                      onAddToCart={handleAddToCart}
                      onQuantityChange={handleQuantityChange}
                      onWeightChange={handleProductWeightChange}
                    />
                  </Col>
                ))
              ) : (
                <Col xs={12} className="text-center py-5">
                  <p className="fs-4">
                    No active combos found at the moment. Please check back
                    later!
                  </p>
                </Col>
              )}
            </Row>
          </div>
        </section>
      </main>

      <FloatingWidgets />
      <Footer />
    </>
  );
}
